var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { login } from "@services/AuthService";
import "@styles/css/login/login.css";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { BeoButton } from "@components/common/action";
import { BeoInputText, BeoPassword } from "@components/common/input";
import { BeoConfirmDialouge } from "@components/common/view";
import { useAppInitialization } from "@hooks/useAppInitialization";
import BeoCaptcha from "@pages/common/components/BeoCaptchaValidator/BeoCaptcha";
import { getUserRole } from "@utils/auth";
import { confirmDialog } from "primereact/confirmdialog";
import { useNavigate } from "react-router-dom";
/**
 * Login component provides a user authentication interface.
 * It handles user login, form validation using `react-hook-form`,
 * and manages session-related scenarios including concurrent sessions.
 *
 * @component
 * @param {LoginProps} props - The component's properties.
 * @author niyas.shajahan
 * @since 2025-06-19
 */
var Login = function (_a) {
    var setIsAuthenticated = _a.setIsAuthenticated;
    /**
     * State to manage the loading status during login attempts.
     * @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]}
     */
    var _b = useState(false), loading = _b[0], setLoading = _b[1];
    /**
     * State to store and display login error messages.
     * @type {[string | null, React.Dispatch<React.SetStateAction<string | null>>]}
     */
    var _c = useState(null), errorMessage = _c[0], setErrorMessage = _c[1];
    /**
     * Initializes `react-hook-form` for form validation and submission.
     * @type {object}
     * @property {function} register - Function to register input fields.
     * @property {object} control - Control object for controlled components.
     * @property {function} handleSubmit - Function to handle form submission.
     * @property {object} errors - Object containing validation errors.
     */
    var _d = useForm({
        defaultValues: {
            password: "",
        },
    }), register = _d.register, control = _d.control, handleSubmit = _d.handleSubmit, watch = _d.watch, errors = _d.formState.errors;
    var navigate = useNavigate();
    var initialMethod = useAppInitialization().initialMethod;
    /**
     * Handles the form submission for user login.
     * Calls the `login` service and processes the response.
     * Manages loading state, error messages, and session handling (including concurrent sessions).
     * @param {AuthRequest} data - The authentication request data (username and password).
     */
    var onSubmit = function (data) { return __awaiter(void 0, void 0, void 0, function () {
        var localShowCaptcha, loginResponse, role, route, attempts, error_1;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    localShowCaptcha = manageShowCaptcha();
                    if (localShowCaptcha && !captchaValid) {
                        setErrorMessage("Please verify the CAPTCHA before logging in.");
                        setIsFailed(true);
                        return [2 /*return*/];
                    }
                    setShowCaptcha(false);
                    setLoading(true); // Set loading to true during API call
                    setErrorMessage(null); // Clear any previous error messages
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 7, 8, 9]);
                    return [4 /*yield*/, login(data)];
                case 2:
                    loginResponse = _b.sent();
                    if (!loginResponse.accessToken) return [3 /*break*/, 5];
                    role = getUserRole();
                    if (!role) return [3 /*break*/, 4];
                    return [4 /*yield*/, initialMethod()];
                case 3:
                    route = _b.sent();
                    // If login is successful (accessToken exists)
                    setFailedAttempts(0); // reset failed attempts on success
                    if (route !== "/unauthorized") {
                        setIsAuthenticated(true); // Update parent component's auth state
                        sessionStorage.setItem("isAuthenticated", "true"); // Store authentication status
                    }
                    navigate(route);
                    _b.label = 4;
                case 4: return [3 /*break*/, 6];
                case 5:
                    if ((_a = loginResponse.error) === null || _a === void 0 ? void 0 : _a.includes("session already active")) {
                        // If a session is already active, prompt for confirmation
                        confirmSessionLogout(data);
                    }
                    else if (loginResponse.error) {
                        attempts = getFailedAttempts() + 1;
                        setFailedAttempts(attempts);
                        setErrorMessage(loginResponse.error);
                    }
                    _b.label = 6;
                case 6: return [3 /*break*/, 9];
                case 7:
                    error_1 = _b.sent();
                    console.error("Login error:", error_1); // Log unexpected errors
                    setErrorMessage("An unexpected error occurred. Please try again."); // Display a generic error message
                    return [3 /*break*/, 9];
                case 8:
                    setLoading(false); // Set loading to false after API call completes
                    return [7 /*endfinally*/];
                case 9: return [2 /*return*/];
            }
        });
    }); };
    /**
     * Displays a confirmation dialog when a session is already active.
     * If the user accepts, it logs out the existing session and retries login.
     * If rejected, it sets an error message.
     * @param {AuthRequest} authRequest - The original authentication request data.
     */
    var confirmSessionLogout = function (authRequest) {
        confirmDialog({
            message: "Do you want to log out from all devices and log in again?",
            header: "Session Already Active",
            icon: "pi pi-exclamation-triangle",
            defaultFocus: "accept",
            accept: function () { return __awaiter(void 0, void 0, void 0, function () {
                return __generator(this, function (_a) {
                    authRequest.forceLogin = true;
                    onSubmit(authRequest); // Retry login after successful logout
                    return [2 /*return*/];
                });
            }); },
            reject: function () {
                setErrorMessage("Login canceled. Please log out from other devices first.");
            },
        });
    };
    var username = watch("username");
    var _e = useState(false), captchaValid = _e[0], setCaptchaValid = _e[1];
    var _f = useState(false), showCaptcha = _f[0], setShowCaptcha = _f[1];
    var _g = useState(false), isFailed = _g[0], setIsFailed = _g[1];
    /**
     * Method to load failed attempts from browser
     * @returns attempts
     */
    var getFailedAttempts = function () {
        var attempts = localStorage.getItem("loginAttempts");
        return attempts ? parseInt(attempts) : 0;
    };
    /**
     * Method to store failed attempts on browser local storage
     * @param count failedAttemots count
     */
    var setFailedAttempts = function (count) {
        localStorage.setItem("loginAttempts", count.toString());
    };
    /**
     * Method to enable or disable captcha
     * @returns isCaptchaEnabled
     */
    var manageShowCaptcha = function () {
        var isAdmin = username.trim() === "admin";
        var attempts = getFailedAttempts();
        var shouldShow = isAdmin || attempts >= 3;
        setShowCaptcha(shouldShow);
        setCaptchaValid(false);
        if (!shouldShow)
            setCaptchaValid(true);
        return shouldShow;
    };
    return (_jsxs("div", { className: "min-h-screen flex items-center justify-center login-page", children: [_jsxs("section", { className: "w-full max-w-md px-4 py-8", children: [_jsx("div", { className: "beo-logo mx-auto mb-8" }), _jsx("main", { className: "backdrop-blur-xl bg-white/75 rounded-xl shadow-2xl p-10 w-full", children: _jsx("div", { className: "space-y-6", children: _jsxs("form", { onSubmit: handleSubmit(onSubmit), className: "space-y-4", children: [_jsx("div", { className: "eakz-logo" }), _jsxs("div", { className: "mb-4", children: [_jsx("label", { htmlFor: "username", className: "block mb-1 text-sm text-gray-900", children: "Username" }), _jsx(BeoInputText, __assign({ id: "username", placeholder: "Enter your username" }, register("username", {
                                                required: "Username is required", // Validation rule
                                            }), { className: "w-full rounded-md border bg-gray-50 ".concat(errors.username ? "border-red-500" : "border-gray-300" // Conditional styling for errors
                                                ), onBlur: manageShowCaptcha })), errors.username && (_jsx("div", { className: "text-red-500 text-[11px] mt-1", children: errors.username.message }))] }), _jsx(Controller, { name: "password", control: control, rules: { required: "Password is required" }, render: function (_a) {
                                            var field = _a.field, fieldState = _a.fieldState;
                                            return (_jsxs("div", { className: "mb-6", children: [_jsx("label", { className: "block mb-1 text-sm text-gray-900", children: "Password" }), _jsx(BeoPassword, __assign({ id: "password", placeholder: "Enter your password" }, field, { invalid: !!fieldState.error, className: "w-full" // this will now work with the above CSS overrides
                                                        , feedback: false, toggleMask: true })), fieldState.error && (_jsx("div", { className: "text-red-500 text-[11px] mt-1", children: fieldState.error.message }))] }));
                                        } }), showCaptcha && (_jsx(BeoCaptcha, { onVerify: function (isValid) { return setCaptchaValid(isValid); }, isFailed: isFailed, onResetFail: function () { return setIsFailed(false); } })), errorMessage && (_jsx("div", { className: "text-red-500 text-xs mb-3 text-center bg-red-900/10 p-2 rounded", children: errorMessage })), _jsxs("div", { className: "flex items-center justify-between mb-6", children: [_jsxs("div", { className: "flex items-center", children: [_jsx("input", { className: "h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded", type: "checkbox", id: "rememberMe" }), _jsx("label", { className: "ml-2 block text-sm text-gray-700", children: "Remember me" })] }), _jsx("div", { className: "text-sm", children: _jsx("a", { href: "/forgot-password", className: "font-medium text-yellow-600 hover:text-yellow-500", children: "Forgot Password?" }) })] }), _jsx(BeoButton, { id: "login-btn", type: "submit", icon: "pi pi-sign-in", className: "w-full py-3 px-4 flex items-center justify-center text-lg font-bold rounded-md text-gray-900 bg-gradient-to-r from-yellow-300 to-orange-400 hover:from-yellow-400 hover:to-orange-500 transition-all duration-300 transform hover:scale-[1.02] shadow-lg", loading: loading, label: "Login", iconPos: "left", children: _jsx("span", { className: "ml-2", children: "\uD83D\uDD12" }) })] }) }) })] }), _jsx(BeoConfirmDialouge, { id: "confirm-dialog" })] }));
};
export default Login;
